import { useState, useEffect } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { onError } from './lib/errorLib';
import { useNavigate, Link, NavLink, useLocation } from 'react-router-dom';
import { AppContext, type Theme, type Density } from './lib/contextLib';
import Routes from './Routes';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
// MUI imports (start migration)
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import AppThemeProvider from './ui/theme';
import { ToastProvider } from './lib/toastContext';
import ErrorHandlerConnector from './components/ErrorHandlerConnector';
import { COLORS } from './lib/appColors';

// Temporary: retain bootstrap classes for layout while migrating incrementally.

function App() {
  const nav = useNavigate();
  const location = useLocation();

  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, userHasAuthenticated] = useState(false);

  useEffect(() => {
    onLoad();
  }, []);

  async function onLoad() {
    try {
      const session = await fetchAuthSession();
      if (session.tokens !== undefined) {
        userHasAuthenticated(true);
      }
    } catch (error) {
      onError(error);
    }

    setIsAuthenticating(false);
  }

  async function handleLogout() {
    await signOut();

    userHasAuthenticated(false);

    nav("/login");
  }

  // Theme handling (persisted)
  const [theme, setTheme] = useState<Theme>(() => {
    try { return localStorage.getItem("mb_theme") === "light" ? "light" : "dark"; } catch { return "dark"; }
  });
  useEffect(() => {
    document.body.classList.toggle("light-theme", theme === "light");
    try { localStorage.setItem("mb_theme", theme); } catch { /* ignore persistence errors */ }
  }, [theme]);

  // Global spacing scale (multiplier for container max width padding, etc.)
  const [density, setDensity] = useState<Density>(() => {
    try {
      const stored = localStorage.getItem("mb_density");
      if (stored === "compact" || stored === "comfortable") return stored;
      return "normal";
    } catch { return "normal"; }
  });
  const densityClass = density === "compact" ? "pad-sm" : density === "comfortable" ? "pad-lg" : "pad-md";
  useEffect(() => { try { localStorage.setItem("mb_density", density); } catch { /* ignore persistence errors */ } }, [density]);

  // Savings rate target (persisted)
  const [savingsTarget, setSavingsTarget] = useState<number>(() => {
    try { const v = localStorage.getItem("mb_savings_target"); return v ? Number(v) : 20; } catch { return 20; }
  });
  useEffect(() => { try { localStorage.setItem("mb_savings_target", String(savingsTarget)); } catch { /* ignore */ } }, [savingsTarget]);

  // (Settings logic moved to dedicated Settings page component)
  // settingsRef no longer used after modal removal

  // Mobile menu anchor
  const [navAnchor, setNavAnchor] = useState<null | HTMLElement>(null);
  const openNav = (e: React.MouseEvent<HTMLElement>) => setNavAnchor(e.currentTarget);
  const closeNav = () => setNavAnchor(null);
  const currentSection = (() => {
    if (location.pathname.startsWith('/budget')) return 'Budget';
    if (location.pathname.startsWith('/portfolio')) return 'Portfolio';
  if (location.pathname.startsWith('/notes')) return 'Notes';
  if (location.pathname.startsWith('/tools')) return 'Tools';
    return '';
  })();

  // Accent colors for top-level nav highlighting
  const accents: Record<string,string> = {
    budget: COLORS.income,
    portfolio: COLORS.expense,
    notes: COLORS.notes,
    tools: COLORS.savings,
  };

  interface NavItem { key: 'budget'|'portfolio'|'notes'|'tools'; to: string; label: string; active: boolean; }
  const navItems: NavItem[] = [
    { key: 'budget', to: '/budget/', label: 'Budget', active: location.pathname.startsWith('/budget') },
    { key: 'portfolio', to: '/portfolio/', label: 'Portfolio', active: location.pathname.startsWith('/portfolio') },
    { key: 'notes', to: '/notes/', label: 'Notes', active: location.pathname.startsWith('/notes') },
    { key: 'tools', to: '/tools/', label: 'Tools', active: location.pathname.startsWith('/tools') }
  ];

  const navButtonSx = (active:boolean, accent:string) => ({
    position: 'relative',
    fontWeight: active ? 600 : 500,
    textTransform: 'uppercase',
    letterSpacing: '.55px',
    padding:'6px 14px',
    borderRadius: '28px',
    lineHeight:1.1,
    backgroundColor: theme === 'light' && active ? '#2563eb' : 'transparent',
    color: theme === 'light'
      ? (active ? '#ffffff' : '#334155')
      : (active ? accent : 'rgba(255,255,255,0.72)'),
    border: theme === 'light' ? '1px solid ' + (active ? '#2563eb' : '#c5d0dc') : '1px solid transparent',
    '&:hover': {
      backgroundColor: theme === 'light'
        ? (active ? '#1d4ed8' : '#f1f5f9')
        : 'rgba(255,255,255,0.08)',
      color: theme === 'light'
        ? (active ? '#fff' : '#1e293b')
        : accent,
      borderColor: theme === 'light' ? (active ? '#1d4ed8' : '#94a3b8') : 'transparent'
    },
    '&:after': {
      content:'""', position:'absolute', left:10, right:10, bottom:2, height: theme==='light'?0:(active ? 3 : 0),
      borderRadius:2, backgroundColor: accent, transition:'height .18s ease'
    }
  });

  // Dynamically sync nav/submenu width to primary table (or default)
  // Removed dynamic width syncing (caused flicker); now using a fixed responsive CSS var in App.css

  return (
    !isAuthenticating && (
      <AppThemeProvider mode={theme === 'light' ? 'light' : 'dark'} density={density}>
        <div className={`App container py-3 ${densityClass}`}>
          <AppBar position="static" color="transparent" elevation={0} sx={{ mb: 2, background:'transparent' }}>
            <Toolbar variant="dense" disableGutters sx={{ display: 'flex', justifyContent:'center' }}>
              <div className="nav-surface" style={{width:'100%'}}>
                {isAuthenticated && (
                  <IconButton color="inherit" edge="start" onClick={openNav} sx={{ display: { xs: 'inline-flex', md: 'none' } }} aria-label="Open navigation menu">
                    <span style={{fontSize:18}}>☰</span>
                  </IconButton>
                )}
                <Typography variant="h6" component={Link} to="/" style={{ textDecoration: 'none', color: 'inherit', fontWeight: 600 }} className="brand">
                  Making Budgets
                </Typography>
                {isAuthenticated && currentSection && (
                  <Typography variant="caption" sx={{ display: { xs: 'inline', md: 'none' }, ml: 1, opacity: 0.8 }}>
                    {currentSection}
                  </Typography>
                )}
                <Box className="main-links" sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: .5 }} role="navigation" aria-label="Main navigation">
                  {isAuthenticated && navItems.map(item => (
                    <Button
                      key={item.key}
                      component={NavLink as any}
                      to={item.to}
                      disableElevation
                      aria-label={`Navigate to ${item.label}`}
                      aria-current={item.active ? 'page' : undefined}
                      sx={navButtonSx(item.active, accents[item.key])}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>
                <Box className="actions" sx={{ display: 'flex', gap: 1 }}>
                  {isAuthenticated ? (
                    <>
                      <Button size="small" color="inherit" component={NavLink as any} to="/settings">Settings</Button>
                      <Button size="small" color="inherit" onClick={handleLogout}>Logout</Button>
                    </>
                  ) : (
                    <>
                      <Button size="small" color="inherit" component={NavLink as any} to="/signup">Signup</Button>
                      <Button size="small" color="inherit" component={NavLink as any} to="/login">Login</Button>
                    </>
                  )}
                </Box>
              </div>
            </Toolbar>
          </AppBar>
          <Menu anchorEl={navAnchor} open={Boolean(navAnchor)} onClose={closeNav} onClick={closeNav} aria-label="Main navigation" sx={{ display: { xs: 'block', md: 'none' } }}>
            {isAuthenticated && navItems.map(item => (
              <MenuItem key={item.key} component={NavLink as any} to={item.to} sx={{ color: item.active ? accents[item.key] : 'inherit', fontWeight: item.active?600:400 }}>
                {item.label}
              </MenuItem>
            ))}
            {isAuthenticated && <Divider />}
            {isAuthenticated ? [
              <MenuItem key="settings" component={NavLink as any} to="/settings">Settings</MenuItem>,
              <MenuItem key="logout" onClick={handleLogout}>Logout</MenuItem>
            ] : [
              <MenuItem key="signup" component={NavLink as any} to="/signup">Signup</MenuItem>,
              <MenuItem key="login" component={NavLink as any} to="/login">Login</MenuItem>
            ]}
          </Menu>
          <AppContext.Provider value={{ isAuthenticated, userHasAuthenticated, theme, setTheme, density, setDensity, savingsTarget, setSavingsTarget }}>
            <ToastProvider>
              <ErrorHandlerConnector />
              <div className="page-shell">
                <ErrorBoundary>
                  <Routes />
                </ErrorBoundary>
              </div>
            </ToastProvider>
          </AppContext.Provider>
        </div>
      </AppThemeProvider>
    )
  );
}

export default App;